import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import * as React from 'react';
import styled from '@emotion/styled';
import FeedTypeBlock from '@cont/FeedTypeBlock/FeedTypeBlock';
import { isClassFeed as getIsClassFeed } from '@utils/feedTypeHelper';
import { getTopologyInfo } from '@selectors/syllabus';
import {
  getHasTopologyInfo,
  getTopologyPlusCoursesCount
} from '@selectors/topologies';
import {
  getTopicLink,
  getTopicLinkHref,
  getCoursesAsPath,
  getCoursesHref
} from '@models/Topology';
import { PlusTopologySource } from '@constants/index';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import { hasWindowInfoText } from './TopicCardComponents/topicCardUtils';
import TopicGroupCardInfoText from './TopicGroupCardInfoText';
import TopicCardInfoText from './TopicCardComponents/TopicCardInfoText';
import TopicProgress from './TopicCardComponents/TopicProgress';

const InfoTextContainer = styled.div`
  height: 18px;
`;

const getBlockInfo = ({
  goalTopology,
  entityType,
  feedType,
  feedUid,
  isSyllabusPage,
  topicHasInfo,
  topicPlusCoursesCount,
  goalUID
}) => {
  const isClassFeed = getIsClassFeed(entityType);
  const blockInfo = {
    viewAllHref: '',
    viewAllLink: '',
    blockKey: `${feedType}-${feedUid}`
  };
  if (isSyllabusPage) {
    return {
      ...blockInfo,
      ...(isClassFeed && {
        viewAllHref: getTopicLinkHref(),
        viewAllLink: getTopicLink({ uid: feedUid }, goalTopology)
      })
    };
  }
  const shouldShowSeeAllLink =
    isClassFeed && (!topicHasInfo || topicPlusCoursesCount > 3);
  return {
    ...blockInfo,
    ...(shouldShowSeeAllLink && {
      viewAllHref: getCoursesHref(),
      viewAllLink: getCoursesAsPath(goalTopology.slug, goalUID, feedUid)
    })
  };
};

const FeedBlockContainer = ({ item: feed, goalUID, source }) => {
  const extraBlockInfo = feed.extraBlockinfo || {};
  const feedUid = extraBlockInfo.uid || '';
  const entityType = extraBlockInfo.entity_type;
  const goalTopology = useSelector((state) => getTopologyInfo(state, goalUID));
  const isSyllabusPage = source === PlusTopologySource.TOPICS;

  // #region topic-page-data
  const topicHasInfo = useSelector((state) =>
    getHasTopologyInfo(state, feedUid)
  );
  const topicPlusCoursesCount = useSelector((state) =>
    getTopologyPlusCoursesCount(state, feedUid)
  );
  // #endregion

  const { viewAllHref, viewAllLink, blockKey } = getBlockInfo({
    goalTopology,
    entityType,
    feedType: feed.type,
    feedUid,
    isSyllabusPage,
    topicHasInfo,
    topicPlusCoursesCount,
    goalUID
  });

  const isSubscribed = useSelector(getIsSubscribedToPlus(goalUID));
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));

  const feedProps = {
    topic: {
      ...(isSyllabusPage
        ? {
            renderCourses: function topicGroupCardInfoText(
              topic,
              topologyCoursesCount
            ) {
              return (
                <InfoTextContainer>
                  <TopicGroupCardInfoText
                    topic={topic}
                    topologyCoursesCount={topologyCoursesCount}
                    isSubscribed={isSubscribed || isOfflineSubscription}
                  />
                </InfoTextContainer>
              );
            }
          }
        : {
            renderProgress: function renderProgressBar(topic) {
              return <TopicProgress topic={topic} />;
            },
            renderCourses: function topicCardInfoText(topic) {
              return isSubscribed ||
                isOfflineSubscription ||
                hasWindowInfoText(topic) ? (
                <InfoTextContainer>
                  <TopicCardInfoText
                    topic={topic}
                    isSubscribed={isSubscribed || isOfflineSubscription}
                  />
                </InfoTextContainer>
              ) : null;
            }
          }),
      hideIfNoCourses: true
    },
    class: {
      topic: {
        uid: extraBlockInfo.uid,
        name: extraBlockInfo.title
      }
    }
  };

  return (
    <FeedTypeBlock
      goalUID={goalUID}
      goalSlug={goalTopology.slug}
      feedBlock={feed}
      key={blockKey}
      goal={goalTopology}
      type="syllabus"
      viewAllLink={viewAllLink}
      viewAllHref={viewAllHref}
      feedProps={feedProps}
      dataAnalytics="syllabus"
    />
  );
};

FeedBlockContainer.propTypes = {
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired
};

const MemoizedFeedBlockContainer = React.memo(FeedBlockContainer);

export default MemoizedFeedBlockContainer;
